import { ColumnDef } from "@tanstack/react-table"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    formatLoyaltyMoney,
    formatLoyaltyNumber,
    selectLoyaltyAnalyticsLevels,
    setLevels,
} from "@/features/loyalty-analytics"
import {
    getLoyaltyLevelBreakdown,
    ILoyaltyLevelBreakdownResponse,
} from "@/shared/api/loyalty-analytics"
import {
    createLoyaltyLevel,
    deleteLoyaltyLevel,
    IListLoyaltyLevelsResponse,
    ILoyaltyLevel,
    listLoyaltyLevels,
    updateLoyaltyLevel,
} from "@/shared/api/loyalty-levels"
import PencilSvg from "@/shared/assets/images/Pencil.svg"
import TrashSvg from "@/shared/assets/images/Trash.svg"
import notify from "@/shared/lib/notify"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { ActionButton } from "@/shared/ui/action-button"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { getDefaultPeriod } from "@/shared/ui/period-picker"
import { Table } from "@/shared/ui/table"
import { PageHeader } from "@/widgets/page-header"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import { LoyaltyLevelFormValues } from "../model/types"
import { LoyaltyLevelModal } from "./loyalty-level-modal"

const disabledBannerText = "Уровни лояльности для сообщества выключены — кэшбэк не начисляется. Включение выполняет General Admin"

const formatNumber = (value: number): string => {
    return new Intl.NumberFormat("ru-RU", {
        maximumFractionDigits: 2,
    }).format(value)
}

export const LoyaltyLevelsPage = () => {
    const dispatch = useAppDispatch()
    const analyticsLevels = useAppSelector(selectLoyaltyAnalyticsLevels)
    const breakdownRequestId = useRef(0)
    const [data, setData] = useState<IListLoyaltyLevelsResponse | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [listError, setListError] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentLevel, setCurrentLevel] = useState<ILoyaltyLevel | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [levelToDelete, setLevelToDelete] = useState<ILoyaltyLevel | null>(null)
    const [breakdown, setBreakdown] = useState<ILoyaltyLevelBreakdownResponse | null>(null)
    const [isBreakdownLoading, setIsBreakdownLoading] = useState(true)
    const [breakdownError, setBreakdownError] = useState(false)

    const levels = useMemo(() => {
        return [...(data?.levels ?? [])].sort((first, second) => first.level - second.level)
    }, [data])

    const fetchLevels = useCallback(async () => {
        try {
            setIsLoading(true)
            setListError(null)
            const response = await listLoyaltyLevels()
            setData(response.data)
        } catch (error) {
            setData(null)
            setListError(getApiErrorMessage(error))
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchLevels()
    }, [fetchLevels])

    const fetchBreakdown = useCallback(() => {
        const requestId = ++breakdownRequestId.current
        const period = getDefaultPeriod()
        setIsBreakdownLoading(true)
        setBreakdownError(false)

        getLoyaltyLevelBreakdown({ from: period.from, to: period.to })
            .then((response) => {
                if (breakdownRequestId.current !== requestId) return
                setBreakdown(response.data)
                setBreakdownError(false)
            })
            .catch(() => {
                if (breakdownRequestId.current !== requestId) return
                setBreakdown(null)
                setBreakdownError(true)
            })
            .finally(() => {
                if (breakdownRequestId.current === requestId) setIsBreakdownLoading(false)
            })
    }, [])

    useEffect(() => {
        fetchBreakdown()
        return () => {
            breakdownRequestId.current += 1
        }
    }, [fetchBreakdown])

    const breakdownByLevel = useMemo(
        () => new Map((breakdown?.rows ?? []).map((row) => [String(row.level), row])),
        [breakdown]
    )

    const renderBreakdownCell = (level: ILoyaltyLevel, field: "usersCount" | "cashbackAmount") => {
        if (isBreakdownLoading) return <span className="text-[#828282]">…</span>
        if (breakdownError) return <span className="text-sm text-color-danger">ошибка</span>

        const row = breakdownByLevel.get(String(level.level))
        if (!row) {
            if (breakdown?.truncated) {
                return <span className="text-sm text-[#8A5B00]">нет данных</span>
            }

            return field === "usersCount"
                ? <span>{formatLoyaltyNumber("0")}</span>
                : <span>{formatLoyaltyMoney("0.00")}</span>
        }

        return field === "usersCount"
            ? <span>{formatLoyaltyNumber(row.usersCount)}</span>
            : <span>{formatLoyaltyMoney(row.cashbackAmount)}</span>
    }

    const openCreateModal = () => {
        setCurrentLevel(null)
        setIsModalOpen(true)
    }

    const openEditModal = (level: ILoyaltyLevel) => {
        setCurrentLevel(level)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        if (isSubmitting) {
            return
        }

        setIsModalOpen(false)
        setCurrentLevel(null)
    }

    const closeModalAfterSuccess = () => {
        setIsModalOpen(false)
        setCurrentLevel(null)
    }

    const handleSubmit = async (values: LoyaltyLevelFormValues) => {
        const icon = values.icon?.[0]

        try {
            setIsSubmitting(true)

            if (currentLevel) {
                await updateLoyaltyLevel(currentLevel.level, {
                    name: values.name.trim(),
                    cashback_percent: Number(values.cashback_percent),
                    paid_amount_rub: Number(values.paid_amount_rub),
                    ...(icon ? { icon } : {}),
                })
                notify.success("Уровень успешно обновлен")
            } else {
                await createLoyaltyLevel({
                    level: Number(values.level),
                    name: values.name.trim(),
                    cashback_percent: Number(values.cashback_percent),
                    paid_amount_rub: Number(values.paid_amount_rub),
                    icon: icon as File,
                })
                notify.success("Уровень успешно создан")
            }

            closeModalAfterSuccess()
            fetchLevels()
            fetchBreakdown()
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleConfirmDelete = async () => {
        if (!levelToDelete) {
            return
        }

        try {
            const deletedLevel = levelToDelete.level
            await deleteLoyaltyLevel(deletedLevel)
            notify.success("Уровень удален")
            if (analyticsLevels.includes(deletedLevel)) {
                dispatch(setLevels(analyticsLevels.filter((level) => level !== deletedLevel)))
            }
            setLevelToDelete(null)
            fetchLevels()
            fetchBreakdown()
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        }
    }

    const columns: ColumnDef<ILoyaltyLevel, any>[] = [
        {
            accessorFn: (level) => <div>{level.level}</div>,
            id: "level",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Уровень</div>,
        },
        {
            accessorFn: (level) => <div className="whitespace-normal">{level.name}</div>,
            id: "name",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Название</div>,
        },
        {
            accessorFn: (level) => <div>{formatNumber(level.cashback_percent)} %</div>,
            id: "cashback_percent",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Кэшбэк %</div>,
        },
        {
            accessorFn: (level) => <div>{formatNumber(level.paid_amount_rub)} ₽</div>,
            id: "paid_amount_rub",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Порог ₽</div>,
        },
        {
            accessorFn: (level) => renderBreakdownCell(level, "usersCount"),
            id: "users_count",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Число пользователей</div>,
        },
        {
            accessorFn: (level) => renderBreakdownCell(level, "cashbackAmount"),
            id: "cashback_amount",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Сумма начисленного кэшбэка</div>,
        },
        {
            accessorFn: (level) => {
                const iconUrl = level.icon_file_url

                return iconUrl ? (
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#F7F8FA]">
                        <img src={iconUrl} alt="" className="w-full h-full object-contain" />
                    </div>
                ) : (
                    <div>—</div>
                )
            },
            id: "icon",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Иконка</div>,
        },
        {
            accessorFn: (level) => (
                <div className="w-full flex justify-end gap-2">
                    <ActionButton onClick={() => openEditModal(level)} icon={PencilSvg} title="Редактировать" />
                    <ActionButton onClick={() => setLevelToDelete(level)} icon={TrashSvg} title="Удалить" />
                </div>
            ),
            id: "actions",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div>Действия</div>,
        },
    ]

    return (
        <div className="_page_container">
            <PageHeader title="Уровни лояльности" />
            <div className="flexbox">
                <div className="flex justify-end">
                    <button
                        onClick={openCreateModal}
                        className="h-[52px] w-fit px-6 text-center bg-color-dark text-white rounded-full"
                    >
                        Создать уровень
                    </button>
                </div>

                {data?.["is_enabled"] === false && (
                    <div className="info_panel">
                        {disabledBannerText}
                    </div>
                )}

                {breakdown?.truncated && (
                    <div className="rounded-xl bg-[#FFF4D6] p-4 text-[#8A5B00]">
                        Агрегаты за 30 дней усечены серверным лимитом
                    </div>
                )}

                {breakdownError && (
                    <div className="info_panel flex items-center justify-between gap-4 text-sm">
                        <span className="text-color-danger">Не удалось загрузить агрегаты уровней</span>
                        <button
                            type="button"
                            onClick={fetchBreakdown}
                            className="form_handle_button !h-9 !w-auto px-4"
                        >
                            Повторить агрегаты
                        </button>
                    </div>
                )}

                {isLoading && (
                    <div className="info_panel">
                        <Loader />
                    </div>
                )}

                {!isLoading && listError && (
                    <div className="info_panel flex items-center justify-between gap-4">
                        <div className="text-color-danger">{listError}</div>
                        <button onClick={fetchLevels} className="form_handle_button !w-auto px-6">
                            Повторить
                        </button>
                    </div>
                )}

                {!isLoading && !listError && data && levels.length === 0 && (
                    <NoTableData label="Нет данных об уровнях лояльности" />
                )}

                {!isLoading && !listError && data && levels.length > 0 && (
                    <div className="table_box">
                        <Table columns={columns} data={levels} />
                    </div>
                )}
            </div>

            <LoyaltyLevelModal
                isOpen={isModalOpen}
                currentLevel={currentLevel}
                levels={levels}
                isSubmitting={isSubmitting}
                onClose={closeModal}
                onSubmit={handleSubmit}
            />

            <ConfirmModal
                isOpen={Boolean(levelToDelete)}
                title="Удалить уровень"
                message={levelToDelete ? `Уровень "${levelToDelete.name}" будет удален без возможности восстановления. Продолжить?` : ""}
                confirmText="Удалить"
                onConfirm={handleConfirmDelete}
                onCancel={() => setLevelToDelete(null)}
            />
        </div>
    )
}
